import { Container } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import React, { FunctionComponent, useState, ReactNode, useEffect } from 'react'

import { useCheckoutState, useDisplayProcessingModal, useIsAfterpayAppGiftCardFlow, useShowLoaderOnly, useAPFooter, useRegionHasMultiTenant } from '../../state'
import { AppContainer, Footer, PageTransition, OrderComplete, ProcessingModal } from '../../component'
import { CheckoutStatus, Features } from '../../model'
import { useFlag } from '@afterpaytouch/feature'
import styles from '../MainLayout/style.module.css'
import clsx from 'clsx'
import { MaintenanceModeContent } from '../../component/MaintenanceModeContent/MaintenanceModeContent'
import { isNullOrUndefined } from '@afterpay/utils'

interface Props {
  hasMargin?: boolean
  children?: ReactNode
  allowPostCheckout?: boolean
  hasDefaultProcessingTimeout?: boolean
  allowFooter?: boolean
}

export const WhiteLabelLayout: FunctionComponent<Props> = ({
  children,
  hasMargin = true,
  allowPostCheckout = true,
  hasDefaultProcessingTimeout = false,
  allowFooter = true,
}) => {
  const { asPath } = useRouter()
  const { status } = useCheckoutState()
  const [initialStatus] = useState(status)
  const isClosed = initialStatus === CheckoutStatus.Closed
  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow()
  const showLoaderOnly = useShowLoaderOnly()
  const shouldUseAPFooter = useAPFooter()
  const regionHasMultiTenant = useRegionHasMultiTenant()

  const isMaintenanceMode = useFlag(Features.MAINTENANCE_MODE)

  const isConfirmChangeToClose = !isClosed && status === CheckoutStatus.Closed
  const isConfirmed = status === CheckoutStatus.Confirmed || isConfirmChangeToClose
  const displayProcessingModal = useDisplayProcessingModal()

  const containerStyles = clsx(!showLoaderOnly && styles.container)
  const showFooter = allowFooter && !showLoaderOnly && shouldUseAPFooter
  const shouldRenderFooter = !showLoaderOnly && !regionHasMultiTenant
  const [isGiftCardFlow, setIsGiftCardFlow] = useState<boolean | undefined>(undefined)

  // persist gift card flow on processing modal
  useEffect(() => {
    if (isNullOrUndefined(isGiftCardFlow) || isAfterpayAppGiftCardFlow) {
      setIsGiftCardFlow(isAfterpayAppGiftCardFlow)
    }
  }, [isAfterpayAppGiftCardFlow])

  return (
    <Container>
      <AppContainer hasMargin={hasMargin} hasMobileTopMargin={!showLoaderOnly} lockScroll={isConfirmed}>
        {displayProcessingModal && !isClosed && (
          <ProcessingModal allowPostCheckout={allowPostCheckout} hasDefaultTimeout={hasDefaultProcessingTimeout} isAPGiftCardFlow={isGiftCardFlow} />
        )}
        <div className={containerStyles}>
          {!isClosed && !isMaintenanceMode && (
            <PageTransition location={asPath} removeTransition={showLoaderOnly}>
              {children}
            </PageTransition>
          )}
          {isClosed && !isMaintenanceMode && <OrderComplete />}
          {isMaintenanceMode && <MaintenanceModeContent />}
        </div>
        <Footer visible={showFooter} shouldRender={shouldRenderFooter} />
      </AppContainer>
    </Container>
  )
}

export const getLayout = (props?: Props) => (page: any) => <WhiteLabelLayout {...props}>{page}</WhiteLabelLayout>
